import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { portalApi } from '../../services/portal';
import { Invoice, Payment, Credit, InvoiceStatus, PaymentMethod } from '../../types';

interface PortalPayment extends Omit<Payment, 'notes'> {
  // Notes excluded for portal view
}

interface PortalCredit extends Omit<Credit, 'internalNotes'> {
  // Internal notes excluded for portal view
}

interface TransactionItem {
  id: string;
  type: 'payment' | 'credit';
  date: string;
  amount: number;
  description: string;
  paymentMethod?: PaymentMethod;
  reference?: string;
  status: 'active' | 'voided';
  runningBalance: number;
}

export const PortalInvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [payments, setPayments] = useState<PortalPayment[]>([]);
  const [credits, setCredits] = useState<PortalCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadInvoiceData();
    }
  }, [id]);

  const loadInvoiceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const invoiceData = await portalApi.getInvoiceWithPayments(id!);
      setInvoice(invoiceData.invoice);
      setPayments(invoiceData.payments || []);
      setCredits(invoiceData.credits || []);
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setError('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  // Parse YYYY-MM-DD as local date to avoid timezone shift
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  const statusColors: Record<InvoiceStatus, string> = {
    draft: '#9e9e9e',
    sent: '#2196f3',
    paid: '#4caf50',
    overdue: '#f44336',
    cancelled: '#757575',
  };

  const paymentMethodLabels: Record<PaymentMethod, string> = {
    'cash': 'Cash',
    'check': 'Check',
    'credit-card': 'Credit Card',
    'zelle': 'Zelle',
    'venmo': 'Venmo',
    'paypal': 'PayPal',
    'other': 'Other',
  };


  // Build transaction history with running balance
  const buildTransactionHistory = (): TransactionItem[] => {
    const transactions: TransactionItem[] = [];

    // Add payments
    payments.forEach(p => {
      transactions.push({
        id: p.paymentId,
        type: 'payment',
        date: p.paymentDate,
        amount: p.amount,
        description: `Payment - ${paymentMethodLabels[p.paymentMethod]}`,
        paymentMethod: p.paymentMethod,
        reference: p.reference,
        status: p.status,
        runningBalance: 0, // Will be calculated
      });
    });

    // Add credits (without internal notes)
    credits.forEach(c => {
      transactions.push({
        id: c.creditId,
        type: 'credit',
        date: c.createdAt,
        amount: c.amount,
        description: `Credit - ${c.reason}`,
        status: c.status,
        runningBalance: 0, // Will be calculated
      });
    });

    // Sort by date
    transactions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Calculate running balance
    let balance = invoice?.total || 0;
    transactions.forEach(t => {
      if (t.status === 'active') {
        balance -= t.amount;
      }
      t.runningBalance = balance;
    });

    return transactions;
  };

  if (loading) {
    return <Layout><div style={{ padding: '24px' }}>Loading...</div></Layout>;
  }

  if (error || !invoice) {
    return (
      <Layout>
        <div style={{ padding: '24px' }}>
          <button onClick={() => navigate('/portal/dashboard')} style={{ marginBottom: '16px', padding: '8px 16px', cursor: 'pointer' }}>
            ← Back to Dashboard
          </button>
          <div style={{ color: '#f44336' }}>{error || 'Invoice not found'}</div>
        </div>
      </Layout>
    );
  }

  const transactions = buildTransactionHistory();

  return (
    <Layout>
      <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
        <button 
          onClick={() => navigate('/portal/dashboard')} 
          style={{ 
            marginBottom: '16px', 
            padding: '8px 16px', 
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        >
          ← Back to Dashboard
        </button>

        {/* Invoice Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '24px'
        }}>
          <div>
            <h1 style={{ margin: 0, marginBottom: '8px' }}>Invoice {invoice.invoiceNumber}</h1>
            <div style={{ color: '#666' }}>
              Issued: {formatDate(invoice.issueDate)} • Due: {formatDate(invoice.dueDate)}
            </div>
          </div>
          <span style={{ 
            backgroundColor: statusColors[invoice.status], 
            color: 'white', 
            padding: '6px 16px', 
            borderRadius: '16px', 
            fontSize: '14px', 
            textTransform: 'uppercase',
            fontWeight: 500
          }}>
            {invoice.status}
          </span>
        </div>

        {/* Balance Due Card */}
        <div style={{ 
          backgroundColor: invoice.balanceDue > 0 ? '#fff3e0' : '#e8f5e9',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Balance Due</div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: 'bold', 
              color: invoice.balanceDue > 0 ? '#f57c00' : '#4caf50' 
            }}>
              {formatCurrency(invoice.balanceDue)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>Invoice Total: {formatCurrency(invoice.total)}</div>
            {invoice.paidAmount > 0 && (
              <div style={{ fontSize: '14px', color: '#4caf50' }}>Paid: {formatCurrency(invoice.paidAmount)}</div>
            )}
            {invoice.creditAmount > 0 && (
              <div style={{ fontSize: '14px', color: '#2196f3' }}>Credits: {formatCurrency(invoice.creditAmount)}</div>
            )}
          </div>
        </div>

        {/* Line Items */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          border: '1px solid #e0e0e0',
          marginBottom: '24px'
        }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
            <h3 style={{ margin: 0 }}>Services</h3>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Duration</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{formatDate(item.date)}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{item.description}</td>
                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>
                    {item.duration ? `${item.duration}m` : '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <td colSpan={3} style={{ padding: '12px', textAlign: 'right', fontWeight: 500 }}>Subtotal</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(invoice.subtotal)}</td>
              </tr>
              {invoice.taxAmount > 0 && (
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <td colSpan={3} style={{ padding: '12px', textAlign: 'right' }}>Tax ({invoice.taxRate}%)</td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(invoice.taxAmount)}</td>
                </tr>
              )}
              <tr style={{ backgroundColor: '#e3f2fd' }}>
                <td colSpan={3} style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Total</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(invoice.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Payment History */}
        {transactions.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
              <h3 style={{ margin: 0 }}>Payment History</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Amount</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} style={{ opacity: t.status === 'voided' ? 0.5 : 1 }}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                      {formatDate(t.date)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ 
                        backgroundColor: t.type === 'payment' ? '#e8f5e9' : '#e3f2fd',
                        color: t.type === 'payment' ? '#2e7d32' : '#1565c0',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        textTransform: 'capitalize'
                      }}>
                        {t.type}
                      </span>
                      {t.status === 'voided' && (
                        <span style={{ 
                          backgroundColor: '#ffebee',
                          color: '#c62828',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          marginLeft: '4px'
                        }}>
                          Voided
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                      {t.description}
                      {t.reference && (
                        <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>
                          (Ref: {t.reference})
                        </span>
                      )}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      borderBottom: '1px solid #e0e0e0',
                      color: '#4caf50',
                      fontWeight: 500
                    }}>
                      -{formatCurrency(t.amount)}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: 500
                    }}>
                      {formatCurrency(t.runningBalance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '8px',
            marginTop: '24px'
          }}>
            <div style={{ fontWeight: 500, marginBottom: '8px' }}>Notes</div>
            <div style={{ color: '#666' }}>{invoice.notes}</div>
          </div>
        )}
      </div>
    </Layout>
  );
};
